/* Three-column drawer menu controller.

   Layout (desktop):
   - Primary column: brand + top-level items.
   - Secondary column: pane for the active top-level item (its children).
   - Tertiary column: pane for the active secondary item (its grandchildren).
     Only visible when a tertiary pane is active. Triggered by clicking a
     `--has-pane` button inside a secondary pane.

   Behavior:
   - Activating a secondary pane resets any active tertiary pane.
   - Activating a tertiary pane keeps the parent secondary pane visible.
   - Hover does nothing on desktop — clicks only.

   Mobile (<750px): drill-down.
   - Tap top-level item → secondary pane replaces primary.
   - Tap secondary item with children → tertiary pane replaces secondary.
   - Back button in tertiary returns to secondary; back in secondary
     returns to primary. */
(function () {
  const MOBILE_BREAKPOINT = 750;
  const RESET_DELAY_MS = 300;

  function isMobile() {
    return window.matchMedia('(max-width: ' + (MOBILE_BREAKPOINT - 1) + 'px)').matches;
  }

  // ID convention: secondary = `kave-pane-{n}`, tertiary = `kave-pane-{n}-{m}`.
  function isTertiary(id) {
    return /^kave-pane-\d+-\d+$/.test(id);
  }

  function bindMenu(root) {
    if (!root || root.dataset.kaveBound === 'true') return;
    root.dataset.kaveBound = 'true';

    const triggers = root.querySelectorAll('[data-kave-pane-trigger]');
    const panes = root.querySelectorAll('.kave-menu__pane');
    const backButtons = root.querySelectorAll('[data-kave-back]');
    const backTertiaryButtons = root.querySelectorAll('[data-kave-back-tertiary]');

    if (!triggers.length || !panes.length) return;

    function activate(targetId) {
      const tert = isTertiary(targetId);

      // Toggle is-active on every trigger; only the matching one stays active.
      triggers.forEach(function (btn) {
        const isActive = btn.dataset.kavePaneTrigger === targetId;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });

      if (tert) {
        // Activating a tertiary pane: only swap among tertiary panes,
        // leave the active secondary pane untouched.
        panes.forEach(function (pane) {
          if (isTertiary(pane.id)) {
            pane.classList.toggle('is-active', pane.id === targetId);
          }
        });
        root.classList.add('is-tertiary-revealed');
      } else {
        // Activating a secondary pane: swap secondary panes AND drop all
        // tertiary panes (the previously open tertiary belonged to another
        // secondary, so it must close).
        panes.forEach(function (pane) {
          if (isTertiary(pane.id)) {
            pane.classList.remove('is-active');
          } else {
            pane.classList.toggle('is-active', pane.id === targetId);
          }
        });
        root.classList.add('is-pane-revealed');
        root.classList.remove('is-tertiary-revealed');
      }
    }

    function openMobilePane(targetId) {
      activate(targetId);
      if (isTertiary(targetId)) {
        root.classList.add('is-mobile-tertiary-open');
      } else {
        root.classList.add('is-mobile-pane-open');
        root.classList.remove('is-mobile-tertiary-open');
      }
    }

    function closeMobilePane() {
      root.classList.remove('is-mobile-pane-open');
      root.classList.remove('is-mobile-tertiary-open');
    }

    function closeMobileTertiary() {
      root.classList.remove('is-mobile-tertiary-open');
    }

    function resetAll() {
      triggers.forEach(function (btn) {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-expanded', 'false');
      });
      panes.forEach(function (pane) { pane.classList.remove('is-active'); });
      root.classList.remove('is-pane-revealed');
      root.classList.remove('is-tertiary-revealed');
      root.classList.remove('is-mobile-pane-open');
      root.classList.remove('is-mobile-tertiary-open');
    }

    triggers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = btn.dataset.kavePaneTrigger;
        if (!targetId) return;
        if (isMobile()) {
          openMobilePane(targetId);
        } else {
          activate(targetId);
        }
      });
    });

    backButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeMobilePane();
      });
    });

    backTertiaryButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeMobileTertiary();
      });
    });

    // When the drawer closes, reset state so the next open starts clean
    // (no active item, no visible right columns).
    const burger = root.closest('burger-menu');
    if (burger) {
      const drawer = burger.querySelector('.burger-menu');
      if (drawer) {
        if (root._kaveObserver) root._kaveObserver.disconnect();
        const observer = new MutationObserver(function () {
          if (!drawer.classList.contains('burger-menu--open')) {
            setTimeout(resetAll, RESET_DELAY_MS);
          }
        });
        observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        root._kaveObserver = observer;
      }
    }
  }

  function init() {
    document.querySelectorAll('.kave-burger .kave-menu').forEach(bindMenu);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
